import { type FC, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Flex,
  HStack,
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  Portal,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { type DateRange } from 'react-day-picker';
import { IoFilter } from 'react-icons/io5';

import {
  IconCalendarOutlineGray,
  IconEmail,
  IconSpecialStars,
  IconStar,
  IconStatistics,
} from '@/components';
import { EmailFilterComponent } from '@/components/Filters/Email/EmailFilterComponent';
import {
  RANGE_MAX_VALUE,
  RANGE_MIN_VALUE,
  RangeFilterComponent,
} from '@/components/Filters/Range/RangeFilterComponent';
import {
  RatingFilterComponent,
  type RatingObj,
} from '@/components/Filters/Rating/RatingFilterComponent';
import { StatusFilterComponent } from '@/components/Filters/Status/StatusFilterComponent';
import { PopoverDayPicker, PopoverItemRow } from '@/components/Popover';
import type { InterviewStatus } from '@/services/api';
import { statusFilterItems } from '@/utils/statusFilterItems';

import { FiltersHeader } from '../../components/Filters/Header';

export type StatusItemsObj = Record<InterviewStatus, boolean>;

export type CandidatesFiltersProps = {
  emailSearchTerm: string;
  setEmailSearchTerm: (value: string | null) => void;
  rangeSearchTerm: Array<number>;
  setRangeSearchTerm: (value: Array<number>) => void;
  status: StatusItemsObj;
  setStatus: (value: StatusItemsObj) => void;
  rating: RatingObj;
  setRating: (value: RatingObj) => void;
  dateRangeSearchTerm: DateRange;
  setDateRangeSearchTerm: (value: DateRange) => void;
  setPage: (page: number) => void;
  clearAllFilters: () => void;
};

export const CandidatesFilters: FC<CandidatesFiltersProps> = ({
  emailSearchTerm,
  setEmailSearchTerm,
  rangeSearchTerm,
  setRangeSearchTerm,
  status,
  setStatus,
  rating,
  setRating,
  dateRangeSearchTerm,
  setDateRangeSearchTerm,
  setPage,
  clearAllFilters,
}) => {
  const [activeFilterType, setActiveFilterType] = useState<FilterTypes>();
  const parentPopover = useDisclosure();
  const childPopover = useDisclosure();
  const [dateRange, setDateRange] = useState<DateRange>(dateRangeSearchTerm);

  const candidatesFilterItems = [
    {
      key: 'Date',
      name: t('components:candidates.filters.date'),
      icon: <IconCalendarOutlineGray fontSize="20px" mr={2} />,
      marked:
        Boolean(dateRangeSearchTerm.from) && Boolean(dateRangeSearchTerm.to),
    },
    {
      key: 'Email',
      name: t('components:candidates.filters.email'),
      icon: <IconEmail fontSize="20px" mr={2} />,
      marked: Boolean(emailSearchTerm),
    },
    {
      key: 'Status',
      name: t('components:candidates.filters.status'),
      icon: <IconStatistics fontSize="20px" mr={2} />,
      marked: Object.values(status).some(Boolean),
    },
    {
      key: 'Rating',
      name: t('components:candidates.filters.rating'),
      icon: <IconStar fontSize="20px" mr={2} />,
      marked: Object.values(rating).some(Boolean),
    },
    {
      key: 'AI Score',
      name: t('components:candidates.filters.aiScore'),
      icon: <IconSpecialStars fontSize="20px" mr={2} />,
      marked:
        rangeSearchTerm[0] !== RANGE_MIN_VALUE ||
        rangeSearchTerm[1] !== RANGE_MAX_VALUE,
    },
  ] as const;

  type FilterTypes = (typeof candidatesFilterItems)[number]['key'];

  const handleCloseAllPopovers = () => {
    parentPopover.onClose();
    childPopover.onClose();
  };

  const handlePopoverSwitch = (filterType: FilterTypes) => {
    setActiveFilterType(filterType);
    if (!childPopover.isOpen) {
      childPopover.onOpen();
    }
  };

  const filtersAppliedN = useMemo(() => {
    const appliedChecks = [
      Boolean(emailSearchTerm),
      Boolean(dateRangeSearchTerm.from) && Boolean(dateRangeSearchTerm.to),
      rangeSearchTerm[0] !== RANGE_MIN_VALUE ||
        rangeSearchTerm[1] !== RANGE_MAX_VALUE,
      Object.values(status).some(Boolean),
      Object.values(rating).some(Boolean),
    ];

    return appliedChecks.filter(Boolean).length;
  }, [emailSearchTerm, dateRangeSearchTerm, rangeSearchTerm, status, rating]);

  const onSubmitDateRange = (dateRange: DateRange) => {
    setPage(1);
    setDateRangeSearchTerm(dateRange);
    handleCloseAllPopovers();
  };

  return (
    <HStack>
      <Popover
        closeOnEsc
        closeOnBlur
        placement="bottom-start"
        returnFocusOnClose={false}
        isOpen={parentPopover.isOpen}
        onClose={handleCloseAllPopovers}
      >
        <PopoverAnchor>
          <Button
            size="md"
            leftIcon={<IoFilter />}
            rightIcon={
              filtersAppliedN ? (
                <Text color="brand.500" fontSize="md" fontWeight="semibold">
                  ({filtersAppliedN})
                </Text>
              ) : undefined
            }
            boxShadow={parentPopover.isOpen ? '0 0 0 4px #F0E9F8' : 'none'}
            onClick={
              parentPopover.isOpen
                ? handleCloseAllPopovers
                : parentPopover.onOpen
            }
          >
            {t('common:actions.filter')}
          </Button>
        </PopoverAnchor>
        <Portal>
          <PopoverContent
            w="200px"
            borderRadius="xl"
            borderColor="gray.100"
            shadow="md"
          >
            <PopoverBody p={0}>
              <Popover
                closeOnBlur
                placement="right-start"
                isOpen={childPopover.isOpen}
                onClose={childPopover.onClose}
              >
                <PopoverAnchor>
                  <Flex direction="column">
                    <FiltersHeader
                      filtersAppliedN={filtersAppliedN}
                      onClear={() => {
                        clearAllFilters();
                        handleCloseAllPopovers();
                      }}
                      onClose={handleCloseAllPopovers}
                    />
                    {candidatesFilterItems.map((item) => {
                      return (
                        <PopoverItemRow
                          key={item.key}
                          name={item.name}
                          icon={item.icon}
                          onRowClick={() => handlePopoverSwitch(item.key)}
                          marked={item.marked}
                        />
                      );
                    })}
                  </Flex>
                </PopoverAnchor>

                <PopoverContent shadow="md">
                  {activeFilterType === 'Email' ? (
                    <EmailFilterComponent
                      emailInitialValue={emailSearchTerm}
                      onSubmit={(newEmail) => {
                        setPage(1);
                        setEmailSearchTerm(newEmail);
                      }}
                      onclose={handleCloseAllPopovers}
                    />
                  ) : activeFilterType === 'Date' ? (
                    <Box pr={2}>
                      <PopoverDayPicker
                        wrappedInPopoverContent={false}
                        canDeselectSelectedDate
                        dayPickerMode="range"
                        dateRangeValue={dateRange}
                        setDateRangeValue={(rangeDate) =>
                          setDateRange(rangeDate!)
                        }
                        onSubmit={(rangeDate) => onSubmitDateRange(rangeDate!)}
                        isFilter
                      />
                    </Box>
                  ) : activeFilterType === 'Status' ? (
                    <StatusFilterComponent<InterviewStatus>
                      initialValues={status}
                      onSubmit={(newData) => {
                        setPage(1);
                        setStatus(newData);
                      }}
                      onclose={handleCloseAllPopovers}
                      statusFilterItems={statusFilterItems}
                    />
                  ) : activeFilterType === 'Rating' ? (
                    <RatingFilterComponent
                      ratingDefaultValues={rating}
                      onSubmit={(newRating) => {
                        setPage(1);
                        setRating(newRating);
                      }}
                      onclose={handleCloseAllPopovers}
                    />
                  ) : activeFilterType === 'AI Score' ? (
                    <RangeFilterComponent
                      rangeInitialValue={rangeSearchTerm}
                      onSubmit={(newMin, newMax) => {
                        setPage(1);
                        setRangeSearchTerm([newMin, newMax]);
                      }}
                      onclose={handleCloseAllPopovers}
                    />
                  ) : null}
                </PopoverContent>
              </Popover>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    </HStack>
  );
};
